/**
 * HA Bridge — Home Assistant REST API ile doğrudan iletişim katmanı.
 *
 * Aktivasyon: .env.local dosyasına şunu ekle:
 *   VITE_HA_URL=http://192.168.1.XXX:8123
 *   VITE_HA_TOKEN=eyJ...  (HA → Profil → Security → Long-Lived Access Tokens)
 *
 * Entity ID eşlemesi (Developer Tools → States):
 *   Çadır içi (MT15/MT29 - INSIDE TENT):
 *     sensor.mt15_mt29_inside_tent_temperature    → temperature (°C)
 *     sensor.mt15_mt29_inside_tent_humidity       → humidity (%)
 *     sensor.mt15_mt29_inside_tent_carbon_dioxide → co2 (ppm)
 *     sensor.mt15_mt29_inside_tent_formaldehyde   → hcho (mg/m³)
 *     sensor.mt15_mt29_inside_tent_pm2_5          → pm25 (μg/m³)
 *     sensor.mt15_mt29_inside_tent_pm10           → pm10 (μg/m³)
 *     sensor.mt15_mt29_inside_tent_air_quality_index → aqi
 *     sensor.mt15_mt29_inside_tent_battery        → sensorBattery (%)
 *   Çadır dışı / oda referans (MT11W - TENT ROOM = tent-2):
 *     sensor.mt11w_tent_room_temperature    → temperature (°C)
 *     sensor.mt11w_tent_room_humidity       → humidity (%)
 *     sensor.mt11w_tent_room_carbon_dioxide → co2 (ppm)
 *
 * API referansı: http://HA_IP:8123/api/
 *   GET  /api/states/{entity_id}              → sensör okuma
 *   POST /api/services/switch/turn_on         → cihaz aç  { entity_id }
 *   POST /api/services/switch/turn_off        → cihaz kapat { entity_id }
 */

import type { SensorReading, DeviceStatus } from '../types';

const HA_URL_LOCAL  = import.meta.env.VITE_HA_URL        as string | undefined;
const HA_URL_REMOTE = import.meta.env.VITE_HA_URL_REMOTE as string | undefined;
const HA_TOKEN      = import.meta.env.VITE_HA_TOKEN      as string | undefined;

export const mockMode = (!HA_URL_LOCAL && !HA_URL_REMOTE) || !HA_TOKEN;

// Resolved at first successful request; starts with local preference.
let resolvedUrl: string | null = HA_URL_LOCAL ?? HA_URL_REMOTE ?? null;

async function resolveHaUrl(): Promise<string> {
  if (!HA_URL_LOCAL) return HA_URL_REMOTE!;
  try {
    const ctrl = new AbortController();
    const t = setTimeout(() => ctrl.abort(), 2000);
    await fetch(`${HA_URL_LOCAL}/api/`, {
      headers: { 'Authorization': `Bearer ${HA_TOKEN}` },
      signal: ctrl.signal,
    });
    clearTimeout(t);
    resolvedUrl = HA_URL_LOCAL;
  } catch {
    resolvedUrl = HA_URL_REMOTE ?? HA_URL_LOCAL!;
  }
  return resolvedUrl!;
}

// ── Entity ID'leri — Çadır içi (MT15/MT29) ───────────────────────────────────
const ENTITY = {
  temperature : 'sensor.mt15_mt29_inside_tent_temperature',
  humidity    : 'sensor.mt15_mt29_inside_tent_humidity',
  co2         : 'sensor.mt15_mt29_inside_tent_carbon_dioxide',
  hcho        : 'sensor.mt15_mt29_inside_tent_formaldehyde',
  pm25        : 'sensor.mt15_mt29_inside_tent_pm2_5',
  pm10        : 'sensor.mt15_mt29_inside_tent_pm10',
  aqi         : 'sensor.mt15_mt29_inside_tent_air_quality_index',
  battery     : 'sensor.mt15_mt29_inside_tent_battery',
} as const;

// ── Entity ID'leri — Çadır dışı / oda referans (MT11W = tent-2) ──────────────
const AMBIENT_ENTITY = {
  temperature : 'sensor.mt11w_tent_room_temperature',
  humidity    : 'sensor.mt11w_tent_room_humidity',
  co2         : 'sensor.mt11w_tent_room_carbon_dioxide',
} as const;

// ── Sistem & Hava Durumu ──────────────────────────────────────────────────────
const SYSTEM_ENTITY = {
  rpiPower    : 'binary_sensor.rpi_power_status',
  weather     : 'weather.forecast_home',
} as const;

// ── Temel fetch yardımcısı ────────────────────────────────────────────────────
async function haFetch<T>(path: string, init?: RequestInit, timeoutMs = 8000): Promise<T> {
  const base = resolvedUrl ?? await resolveHaUrl();
  const ctrl = new AbortController();
  const t = setTimeout(() => ctrl.abort(), timeoutMs);
  try {
    const res = await fetch(`${base}${path}`, {
      headers: {
        'Authorization': `Bearer ${HA_TOKEN}`,
        'Content-Type': 'application/json',
      },
      ...init,
      signal: ctrl.signal,
    });
    if (!res.ok) throw new Error(`HA API ${res.status}: ${path}`);
    return res.json() as Promise<T>;
  } finally {
    clearTimeout(t);
  }
}

interface HAState {
  entity_id: string;
  state: string;
  attributes: Record<string, unknown>;
  last_updated: string;
}

// ── HA Entity Discovery ───────────────────────────────────────────────────────

export interface HAEntity {
  entity_id: string;
  friendly_name: string;
  state: string;
  unit: string | undefined;
  device_class: string | undefined;
  /** Tahmin edilen SensorType */
  guessedType: 'climate' | 'co2_only' | 'weight' | 'camera' | 'light' | 'custom';
  /** Tahmin edilen metrik listesi */
  guessedMetrics: string[];
}

function guessTypeAndMetrics(entity_id: string, device_class?: string, unit?: string): {
  type: HAEntity['guessedType'];
  metrics: string[];
} {
  const id  = entity_id.toLowerCase();
  const dc  = (device_class ?? '').toLowerCase();
  const u   = (unit ?? '').toLowerCase();

  if (dc === 'temperature' || id.includes('temperature') || u === '°c' || u === '°f')
    return { type: 'climate', metrics: ['temperature'] };
  if (dc === 'humidity' || id.includes('humidity'))
    return { type: 'climate', metrics: ['humidity'] };
  if (dc === 'carbon_dioxide' || id.includes('co2') || id.includes('carbon_dioxide') || u === 'ppm')
    return { type: 'co2_only', metrics: ['co2'] };
  if (dc === 'weight' || dc === 'mass' || id.includes('weight') || id.includes('mass') || u.includes('kg') || u.includes('g'))
    return { type: 'weight', metrics: ['weight'] };
  if (dc === 'illuminance' || id.includes('illuminan') || id.includes('lux') || u === 'lx' || u === 'lm')
    return { type: 'light', metrics: ['lux'] };
  if (id.includes('pm2') || id.includes('pm10') || dc === 'pm25' || dc === 'pm10')
    return { type: 'climate', metrics: ['pm25'] };
  if (id.includes('air_quality') || dc === 'aqi')
    return { type: 'climate', metrics: ['aqi'] };
  return { type: 'custom', metrics: [] };
}

/**
 * HA'daki tüm sensor.* ve binary_sensor.* entity'lerini döndürür.
 *
 * Strateji (sırayla dener):
 *   1. Aynı ağdaysa direkt HA REST API (/api/states)
 *   2. Direkt başarısız → Supabase ha-proxy üzerinden (HTTPS mixed-content çözümü)
 *
 * HA yoksa (mockMode) boş dizi döner.
 */
export async function fetchHAEntities(): Promise<HAEntity[]> {
  // mockMode: ne HA_URL ne HA_TOKEN var — proxy de çalışmaz
  // Ancak SUPABASE_URL varsa proxy denenebilir (proxy HA_URL'yi kendi env'inden okur)
  const canTryProxy = !!(SUPABASE_URL && SUPABASE_ANON);

  if (mockMode && !canTryProxy) return [];

  const parseStates = (all: HAState[]): HAEntity[] =>
    all
      .filter(s =>
        (s.entity_id.startsWith('sensor.') || s.entity_id.startsWith('binary_sensor.')) &&
        s.state !== 'unavailable' && s.state !== 'unknown'
      )
      .map(s => {
        const attr = s.attributes;
        const unit = attr['unit_of_measurement'] as string | undefined;
        const dc   = attr['device_class'] as string | undefined;
        const friendly = (attr['friendly_name'] as string | undefined) ?? s.entity_id;
        const { type, metrics } = guessTypeAndMetrics(s.entity_id, dc, unit);
        return {
          entity_id:      s.entity_id,
          friendly_name:  friendly,
          state:          s.state,
          unit,
          device_class:   dc,
          guessedType:    type,
          guessedMetrics: metrics,
        };
      })
      .sort((a, b) => a.friendly_name.localeCompare(b.friendly_name, 'tr'));

  // 1. Direkt HA dene (aynı ağdayken)
  if (!mockMode) {
    if (!resolvedUrl) await resolveHaUrl();
    try {
      const all = await haFetch<HAState[]>('/api/states', undefined, 8000);
      const result = parseStates(all);
      if (result.length > 0) return result;
    } catch (e) {
      console.info('[HABridge] fetchHAEntities direkt başarısız, proxy deneniyor:', e);
    }
  }

  // 2. Supabase ha-proxy üzerinden dene
  if (!canTryProxy) return [];
  try {
    const res = await fetch(`${SUPABASE_URL}/functions/v1/ha-proxy`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${SUPABASE_ANON}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ action: 'list_states' }),
    });
    if (!res.ok) {
      console.warn('[HABridge] ha-proxy list_states HTTP hatası:', res.status);
      return [];
    }
    const data = await res.json() as { ok: boolean; entities?: HAState[]; error?: string };
    if (!data.ok || !data.entities) {
      console.warn('[HABridge] ha-proxy list_states hatası:', data.error);
      return [];
    }
    return parseStates(data.entities);
  } catch (e) {
    console.warn('[HABridge] ha-proxy fetchHAEntities hatası:', e);
    return [];
  }
}

async function getState(entityId: string): Promise<HAState> {
  return haFetch<HAState>(`/api/states/${entityId}`);
}

// ── Sensör geçerlilik aralıkları ─────────────────────────────────────────────
// Aralık dışı değerler sensör arızasına işaret eder; null olarak işaretlenir.
const SENSOR_RANGES = {
  temperature : { min: -10, max: 60 },   // °C
  humidity    : { min: 0,   max: 100 },  // %
  co2         : { min: 200, max: 5000 }, // ppm
  tvoc        : { min: 0,   max: 60000 },// ppb
  hcho        : { min: 0,   max: 10 },   // mg/m³
  pm25        : { min: 0,   max: 1000 }, // μg/m³
  pm10        : { min: 0,   max: 1000 }, // μg/m³
  battery     : { min: 0,   max: 100 },  // %
};

function validated(metric: keyof typeof SENSOR_RANGES, raw: number): number | undefined {
  const range = SENSOR_RANGES[metric];
  return isFinite(raw) && raw >= range.min && raw <= range.max ? raw : undefined;
}

// ── Sensör okuma ──────────────────────────────────────────────────────────────
export async function fetchCurrentReading(): Promise<SensorReading | null> {
  if (mockMode) return null;
  if (!resolvedUrl) await resolveHaUrl();
  try {
    // Promise.allSettled: tek bir sensör hatası tüm okumayı bloklamaz
    const results = await Promise.allSettled([
      getState(ENTITY.temperature),
      getState(ENTITY.humidity),
      getState(ENTITY.co2),
      getState(ENTITY.hcho),
      getState(ENTITY.pm25),
      getState(ENTITY.pm10),
      getState(ENTITY.aqi),
      getState(ENTITY.battery),
    ]);

    const [tempR, humR, co2R, hchoR, pm25R, pm10R, aqiR, battR] = results;

    // Sıcaklık ve nem olmadan okuma geçersiz
    if (tempR.status === 'rejected' || humR.status === 'rejected') {
      console.warn('[HABridge] Kritik sensör (sıcaklık/nem) okunamadı');
      return null;
    }

    const now = new Date().toISOString();
    const tempState = tempR.value;
    const rawTemp = parseFloat(tempState.state);
    const unit = tempState.attributes['unit_of_measurement'] as string | undefined;
    const tempC = unit === '°F' ? (rawTemp - 32) * 5 / 9 : rawTemp;

    const temperature = validated('temperature', Math.round(tempC * 10) / 10);
    const humidity    = validated('humidity', parseFloat(humR.value.state));

    if (temperature === undefined || humidity === undefined) {
      console.warn('[HABridge] Sıcaklık/nem aralık dışı, okuma atlandı');
      return null;
    }

    const co2Raw  = co2R.status  === 'fulfilled' ? parseFloat(co2R.value.state)   : NaN;
    const hchoRaw = hchoR.status === 'fulfilled' ? parseFloat(hchoR.value.state)  : NaN;
    const pm25Raw = pm25R.status === 'fulfilled' ? parseFloat(pm25R.value.state)  : NaN;
    const pm10Raw = pm10R.status === 'fulfilled' ? parseFloat(pm10R.value.state)  : NaN;
    const battRaw = battR.status === 'fulfilled' ? parseFloat(battR.value.state)  : NaN;

    // Başarısız opsiyonel sensörleri logla
    results.forEach((r, i) => {
      if (i > 1 && r.status === 'rejected') {
        console.warn(`[HABridge] Opsiyonel sensör ${i} okunamadı:`, r.reason);
      }
    });

    return {
      timestamp     : tempState.last_updated ?? now,
      receivedAt    : now,
      temperature,
      humidity,
      co2           : validated('co2', Math.round(co2Raw)) ?? 0,
      hcho          : validated('hcho', hchoRaw),
      pm25          : validated('pm25', pm25Raw),
      pm10          : validated('pm10', pm10Raw),
      aqi           : aqiR.status === 'fulfilled' ? aqiR.value.state : undefined,
      sensorBattery : validated('battery', battRaw),
    };
  } catch (e) {
    console.warn('[HABridge] fetchCurrentReading hatası:', e);
    return null;
  }
}

/**
 * Dış ortam / referans okuması.
 *
 * Öncelik sırası:
 *   1. Zustand store'daki `reference` rolündeki çadırın sensör haEntityId'leri
 *      (Ayarlar → Çadırlar → Rol: Referans)
 *   2. Fallback: AMBIENT_ENTITY sabit değerleri (geriye dönük uyumluluk)
 */
export async function fetchAmbientReading(
  referenceSensors?: Array<{ haEntityId?: string; metrics: string[] }>
): Promise<import('../types').AmbientReading | null> {
  if (mockMode) return null;
  if (!resolvedUrl) await resolveHaUrl();

  // Referans sensörlerden entity_id eşlemesi kur
  const refEntities: { temperature?: string; humidity?: string; co2?: string } = {};

  if (referenceSensors && referenceSensors.length > 0) {
    for (const s of referenceSensors) {
      if (!s.haEntityId) continue;
      const eid = s.haEntityId.toLowerCase();
      const m = s.metrics;
      if (m.includes('temperature') || eid.includes('temperature'))
        refEntities.temperature = s.haEntityId;
      if (m.includes('humidity') || eid.includes('humidity'))
        refEntities.humidity = s.haEntityId;
      if (m.includes('co2') || eid.includes('co2') || eid.includes('carbon_dioxide'))
        refEntities.co2 = s.haEntityId;
    }
  }

  // Fallback to hardcoded ambient entities
  const tempId = refEntities.temperature ?? AMBIENT_ENTITY.temperature;
  const humId  = refEntities.humidity    ?? AMBIENT_ENTITY.humidity;
  const co2Id  = refEntities.co2         ?? AMBIENT_ENTITY.co2;

  try {
    const results = await Promise.allSettled([
      getState(tempId),
      getState(humId),
      getState(co2Id),
    ]);
    const [tempR, humR, co2R] = results;

    if (tempR.status === 'rejected' || humR.status === 'rejected') {
      console.warn('[HABridge] fetchAmbientReading: sıcaklık/nem okunamadı');
      return null;
    }

    const rawTemp = parseFloat(tempR.value.state);
    const unit = tempR.value.attributes['unit_of_measurement'] as string | undefined;
    const temperature = unit === '°F'
      ? Math.round(((rawTemp - 32) * 5 / 9) * 10) / 10
      : rawTemp;

    return {
      temperature,
      humidity  : parseFloat(humR.value.state),
      co2       : co2R.status === 'fulfilled' ? parseFloat(co2R.value.state) : 0,
      updatedAt : new Date().toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit' }),
    };
  } catch (e) {
    console.warn('[HABridge] fetchAmbientReading hatası:', e);
    return null;
  }
}

export async function fetchOutdoorWeather(): Promise<import('../types').OutdoorWeather | null> {
  if (mockMode) return null;
  if (!resolvedUrl) await resolveHaUrl();
  try {
    const state = await getState(SYSTEM_ENTITY.weather);
    const a = state.attributes;
    return {
      city       : 'İstanbul',
      temperature: a['temperature'] as number,
      humidity   : a['humidity'] as number,
      condition  : state.state,
      dewPoint   : a['dew_point'] as number | undefined,
      pressure   : a['pressure'] as number | undefined,
      windSpeed  : a['wind_speed'] as number | undefined,
      uvIndex    : a['uv_index'] as number | undefined,
    };
  } catch (e) {
    console.warn('[HABridge] fetchOutdoorWeather hatası:', e);
    return null;
  }
}

export async function fetchRpiStatus(): Promise<boolean | null> {
  if (mockMode) return null;
  if (!resolvedUrl) await resolveHaUrl();
  try {
    const state = await getState(SYSTEM_ENTITY.rpiPower);
    // binary_sensor problem tipi: off = sorun yok, on = sorun var
    return state.state !== 'on';
  } catch (e) {
    console.warn('[HABridge] fetchRpiStatus hatası:', e);
    return null;
  }
}

// ── Cihaz durumları ───────────────────────────────────────────────────────────
export async function fetchDevices(): Promise<DeviceStatus[] | null> {
  if (mockMode) return null;
  try {
    // haEntityId dolu olan cihazların HA'daki durumunu çek
    // Cihaz listesi store'da tanımlı; burada sadece isActive senkronizasyonu yapılır.
    // haEntityId'si olmayan (offline/standalone) cihazlar store state'inden gelir.
    return null; // store kendi listesini yönetir, HA'dan device listesi çekmiyoruz
  } catch (e) {
    console.warn('[HABridge] fetchDevices hatası:', e);
    return null;
  }
}

// ── Cihaz toggle ─────────────────────────────────────────────────────────────

// Supabase ha-proxy Edge Function URL
const SUPABASE_URL   = import.meta.env.VITE_SUPABASE_URL   as string | undefined;
const SUPABASE_ANON  = import.meta.env.VITE_SUPABASE_ANON_KEY as string | undefined;

/**
 * Supabase ha-proxy üzerinden cihaz toggle (HTTPS→HTTP mixed content çözümü).
 * Production'da (app.gurmemantar.com) direkt HA erişimi mümkün değil.
 */
async function toggleViaProxy(haEntityId: string, turnOn: boolean): Promise<boolean> {
  if (!SUPABASE_URL || !SUPABASE_ANON) return false;
  try {
    const res = await fetch(`${SUPABASE_URL}/functions/v1/ha-proxy`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${SUPABASE_ANON}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        entityId: haEntityId,
        action: turnOn ? 'turn_on' : 'turn_off',
      }),
    });
    const data = await res.json() as { ok: boolean; error?: string };
    if (!data.ok) console.warn('[HABridge] proxy toggle hatası:', data.error);
    return data.ok;
  } catch (e) {
    console.warn('[HABridge] toggleViaProxy hatası:', e);
    return false;
  }
}

export async function toggleDeviceRemote(
  id: string,
  haEntityId: string,
  turnOn: boolean
): Promise<boolean> {
  if (mockMode) return false;
  if (!haEntityId) return false;

  // Önce direkt HA bağlantısını dene (aynı ağdayken)
  if (resolvedUrl) {
    try {
      const service = turnOn ? 'turn_on' : 'turn_off';
      await haFetch(`/api/services/switch/${service}`, {
        method: 'POST',
        body: JSON.stringify({ entity_id: haEntityId }),
      });
      return true;
    } catch (e) {
      console.warn(`[HABridge] toggleDevice direkt (${id}) başarısız, proxy deneniyor:`, e);
    }
  }

  // Direkt başarısız → Supabase proxy üzerinden dene
  const proxyResult = await toggleViaProxy(haEntityId, turnOn);
  if (!proxyResult) {
    console.warn(`[HABridge] toggleDevice proxy (${id}) de başarısız`);
  }
  return proxyResult;
}


// ── Geçmiş okumalar (HA History API) ─────────────────────────────────────────
/**
 * HA History API: GET /api/history/period/{start_time}
 * filter_entity_id ile birden fazla entity tek çağrıda alınır.
 * Dönen format: [ [stateObj, ...], [stateObj, ...], ... ] — entity başına bir dizi.
 */
interface HAHistoryState {
  state: string;
  last_updated: string;
}

export async function fetchHistoryReadings(hours: number): Promise<SensorReading[]> {
  if (mockMode) return [];
  if (!resolvedUrl) await resolveHaUrl();

  try {
    const start = new Date(Date.now() - hours * 3600 * 1000).toISOString();
    const entities = [ENTITY.temperature, ENTITY.humidity, ENTITY.co2].join(',');
    const url = `/api/history/period/${start}?filter_entity_id=${entities}&minimal_response=true&no_attributes=true&significant_changes_only=false`;
    const raw = await haFetch<HAHistoryState[][]>(url);

    if (!Array.isArray(raw) || raw.length < 3) return [];

    // Her entity için timestamp → value map oluştur
    const tempMap = new Map<string, number>();
    const humMap  = new Map<string, number>();
    const co2Map  = new Map<string, number>();

    const indexByEntity: Record<string, Map<string, number>> = {
      [ENTITY.temperature]: tempMap,
      [ENTITY.humidity]:    humMap,
      [ENTITY.co2]:         co2Map,
    };

    // HA, entity sırasını garanti etmez — entity_id ile eşleştir
    for (const entityStates of raw) {
      if (!entityStates?.length) continue;
      // minimal_response'da ilk eleman entity_id taşır
      const firstFull = entityStates[0] as any;
      const entityId: string = firstFull?.entity_id ?? '';
      const map = indexByEntity[entityId];
      if (!map) continue;

      for (const s of entityStates) {
        const v = parseFloat(s.state);
        if (!isNaN(v)) map.set(s.last_updated, v);
      }
    }

    // Tüm timestamp'leri birleştir, eksik değerleri önceki değerle doldur
    const allTimes = [...new Set([
      ...tempMap.keys(), ...humMap.keys(), ...co2Map.keys(),
    ])].sort();

    let lastTemp = NaN, lastHum = NaN, lastCo2 = NaN;
    const readings: SensorReading[] = [];

    for (const ts of allTimes) {
      if (tempMap.has(ts)) lastTemp = tempMap.get(ts)!;
      if (humMap.has(ts))  lastHum  = humMap.get(ts)!;
      if (co2Map.has(ts))  lastCo2  = co2Map.get(ts)!;
      if (isNaN(lastTemp) || isNaN(lastHum) || isNaN(lastCo2)) continue;

      const rawTemp = lastTemp;
      // Sıcaklık birimi kontrolü (°F → °C)
      const temp = rawTemp > 50 ? Math.round(((rawTemp - 32) * 5 / 9) * 10) / 10 : rawTemp;

      readings.push({
        timestamp:   ts,
        receivedAt:  ts,
        temperature: Math.round(temp * 10) / 10,
        humidity:    Math.round(lastHum * 10) / 10,
        co2:         Math.round(lastCo2),
      });
    }

    return readings;
  } catch (e) {
    console.warn('[HABridge] fetchHistoryReadings hatası:', e);
    return [];
  }
}

/**
 * Polling-based "stream" — HA REST API'de SSE endpoint yok.
 * Her intervalMs'de bir anlık okuma yapar ve callback'i çağırır.
 */
export function subscribeToStream(
  onReading: (r: SensorReading) => void,
  _onError?: (e: Event) => void,
  intervalMs = 30_000
): () => void {
  if (mockMode) return () => {};

  let active = true;

  const poll = async () => {
    if (!active) return;
    const reading = await fetchCurrentReading();
    if (reading && active) onReading(reading);
  };

  poll(); // ilk okuma hemen
  const id = setInterval(poll, intervalMs);

  return () => {
    active = false;
    clearInterval(id);
  };
}
